/**
 * RegionalExportCharts.jsx
 * Standalone exportable chart components for Regional Focus bar charts and maps.
 * One bar chart + one map component per region, suitable for chart registry & ExportRenderer.
 */

import { useState, useMemo, useEffect } from 'react';
import { useApp } from '../../../context/AppContext';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  ComposableMap,
  Geographies,
  Geography,
} from 'react-simple-maps';
import { getISO3, isCountryInSubRegions, expandSubRegions } from '../../../utils/countryMapping';
import { fetchTopoJSON, getTopoCache, geoToISO3 } from '../shared/constants';

const REGIONS = [
  {
    key: 'africa',
    label: 'Africa',
    color: '#F37021',
    match: ['Western Africa', 'Eastern Africa', 'Middle Africa', 'Northern Africa', 'Southern Africa', 'Sub-Saharan Africa'],
  },
  {
    key: 'americas',
    label: 'The Americas',
    color: '#007DBC',
    match: ['Caribbean', 'Central America', 'South America', 'Northern America', 'Latin America and the Caribbean'],
  },
  {
    key: 'asia',
    label: 'Asia',
    color: '#4CAF50',
    match: ['Southern Asia', 'South-Eastern Asia', 'Eastern Asia', 'Central Asia', 'Western Asia'],
  },
];

const REGION_MAP_CONFIG = {
  africa:   { center: [20, 2],   scale: 350, width: 500, height: 500 },
  americas: { center: [-75, -5], scale: 250, width: 450, height: 550 },
  asia:     { center: [85, 20],  scale: 350, width: 550, height: 450 },
};

function resultInRegion(result, matchList) {
  const matchSet = new Set(matchList);
  if (!result.regions) return false;
  const regions = Array.isArray(result.regions) ? result.regions : [result.regions];
  return regions.some(r => matchSet.has(r));
}

function getTopCountries(results, matchSet, limit = 10) {
  const counts = new Map();
  results.forEach(r => {
    const raw = r.countries;
    if (!raw) return;
    const list = Array.isArray(raw) ? raw : String(raw).split(',').map(s => s.trim());
    list.forEach(c => {
      if (c && isCountryInSubRegions(c, matchSet)) {
        counts.set(c, (counts.get(c) || 0) + 1);
      }
    });
  });
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([country, count]) => ({ country, count }));
}

function getCountryCountMap(results, matchSet) {
  const counts = {};
  results.forEach(r => {
    const raw = r.countries;
    if (!raw) return;
    const list = Array.isArray(raw) ? raw : String(raw).split(',').map(s => s.trim());
    list.forEach(c => {
      if (c && isCountryInSubRegions(c, matchSet)) {
        counts[c] = (counts[c] || 0) + 1;
      }
    });
  });
  return counts;
}

// ── Bar chart component ──

function RegionalBarChart({ regionKey }) {
  const { filteredResults } = useApp();
  const region = REGIONS.find(r => r.key === regionKey);

  const regionResults = useMemo(
    () => filteredResults.filter(r => resultInRegion(r, region.match)),
    [filteredResults, region],
  );

  const regionMatchSet = useMemo(() => expandSubRegions(region.match), [region]);

  const topCountries = useMemo(() => getTopCountries(regionResults, regionMatchSet), [regionResults, regionMatchSet]);

  if (!topCountries.length) {
    return <p className="text-sm text-gray-400 italic py-4 text-center">No country data available.</p>;
  }

  const reversed = [...topCountries].reverse();

  return (
    <div className="p-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Top Countries — {region.label}</h3>
      <ResponsiveContainer width="100%" height={Math.max(180, reversed.length * 28)}>
        <BarChart data={reversed} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 4 }}>
          <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
          <YAxis type="category" dataKey="country" tick={{ fontSize: 11 }} width={130} />
          <Tooltip formatter={(value) => [value, 'Results']} contentStyle={{ fontSize: 12 }} />
          <Bar dataKey="count" fill={region.color} radius={[0, 4, 4, 0]}>
            {reversed.map((_, i) => <Cell key={i} fill={region.color} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Map component ──

function RegionalMapChart({ regionKey }) {
  const { filteredResults } = useApp();
  const region = REGIONS.find(r => r.key === regionKey);

  const [topology, setTopology] = useState(getTopoCache());

  useEffect(() => {
    if (!topology) fetchTopoJSON().then(setTopology);
  }, [topology]);

  const regionResults = useMemo(
    () => filteredResults.filter(r => resultInRegion(r, region.match)),
    [filteredResults, region],
  );

  const regionMatchSet = useMemo(() => expandSubRegions(region.match), [region]);

  const countryCountMap = useMemo(() => getCountryCountMap(regionResults, regionMatchSet), [regionResults, regionMatchSet]);

  const { iso3Counts, maxCount } = useMemo(() => {
    const iso3Counts = {};
    for (const [country, count] of Object.entries(countryCountMap)) {
      const iso3 = getISO3(country);
      if (iso3) iso3Counts[iso3] = (iso3Counts[iso3] || 0) + count;
    }
    const vals = Object.values(iso3Counts);
    return { iso3Counts, maxCount: vals.length > 0 ? Math.max(...vals) : 1 };
  }, [countryCountMap]);

  const getColor = (iso3) => {
    if (!iso3Counts[iso3]) return '#F3F4F6';
    const count = iso3Counts[iso3];
    // Monochromatic: light tint → full region color
    // Ensure minimum opacity so even count=1 is clearly distinct from no-data
    const t = maxCount > 1 ? 0.25 + 0.75 * ((count - 1) / (maxCount - 1)) : 0.6;
    const num = parseInt(region.color.replace('#', ''), 16);
    const cr = (num >> 16) & 0xff, cg = (num >> 8) & 0xff, cb = num & 0xff;
    const r = Math.round(255 + t * (cr - 255));
    const g = Math.round(255 + t * (cg - 255));
    const b = Math.round(255 + t * (cb - 255));
    return `rgb(${r},${g},${b})`;
  };

  if (!topology) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="w-6 h-6 border-2 border-cgiar-green border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const cfg = REGION_MAP_CONFIG[regionKey];

  return (
    <div className="p-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Results Map — {region.label}</h3>
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{ center: cfg.center, scale: cfg.scale }}
        width={cfg.width}
        height={cfg.height}
        style={{ width: '100%', height: 'auto', maxHeight: 420 }}
      >
        <Geographies geography={topology}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const iso3 = geoToISO3(geo);
              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={getColor(iso3)}
                  stroke="#fff"
                  strokeWidth={0.5}
                  style={{ default: { outline: 'none' }, hover: { outline: 'none' }, pressed: { outline: 'none' } }}
                />
              );
            })
          }
        </Geographies>
      </ComposableMap>
      {/* Legend */}
      <div className="flex items-center justify-center gap-2 text-xs text-gray-500 mt-2">
        <span className="inline-block w-3 h-3 rounded" style={{ background: '#F3F4F6', border: '1px solid #D1D5DB' }} />
        <span>No data</span>
        <div className="w-24 h-3 rounded ml-2" style={{ background: `linear-gradient(to right, ${lightenHex(region.color, 0.75)}, ${region.color})` }} />
        <span>More results</span>
      </div>
    </div>
  );
}

/** Lighten a hex colour toward white by the given amount (0–1). */
function lightenHex(hex, amount) {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.round(((num >> 16) & 0xff) + (255 - ((num >> 16) & 0xff)) * amount);
  const g = Math.round(((num >> 8) & 0xff) + (255 - ((num >> 8) & 0xff)) * amount);
  const b = Math.round((num & 0xff) + (255 - (num & 0xff)) * amount);
  return `rgb(${r},${g},${b})`;
}

// ── Factory wrappers ──

function createBarChart(regionKey) {
  return function ExportBarChart() { return <RegionalBarChart regionKey={regionKey} />; };
}

function createMapChart(regionKey) {
  return function ExportMapChart() { return <RegionalMapChart regionKey={regionKey} />; };
}

export const AfricaBarChart = createBarChart('africa');
export const AmericasBarChart = createBarChart('americas');
export const AsiaBarChart = createBarChart('asia');

export const AfricaMapChart = createMapChart('africa');
export const AmericasMapChart = createMapChart('americas');
export const AsiaMapChart = createMapChart('asia');
